import { AnswerConfig } from "../../grader/grade-input";

/**
 * extractValueFromCell
 *
 * 주어진 HTMLTableCellElement(테이블 셀)에서 값을 추출하여 문자열로 반환합니다.
 *
 * 📌 동작 방식:
 * 1. 셀 내부에 <input> 또는 <select> 요소가 있는지 확인합니다.
 * 2. 있으면 첫 번째 <input> 또는 <select>의 value를 반환합니다.
 * 3. 없으면 해당 셀의 textContent(텍스트 내용)를 반환합니다.
 *
 * @param cell - 값을 추출할 HTML 테이블 셀 요소
 * @returns 추출된 문자열 값
 */
export function extractValueFromCell(cell: HTMLTableCellElement): string {
  // 셀 내부의 <input> 또는 <select> 요소를 모두 찾습니다.
  const inputs = cell.querySelectorAll("input, select");

  // 만약 <input> 또는 <select> 요소가 하나 이상 존재하면,
  if (inputs.length > 0) {
    // 첫 번째 <input> 또는 <select> 요소의 value 값을 가져와 반환합니다.
    // 타입 단언을 통해 TypeScript에게 해당 요소가 HTMLInputElement 또는 HTMLSelectElement임을 명시합니다.
    return (inputs[0] as HTMLInputElement | HTMLSelectElement).value;
  }

  // <input> 또는 <select>가 없으면,
  // 해당 셀의 textContent(셀 내부의 순수 텍스트)를 반환합니다.
  // 만약 textContent가 null인 경우(거의 없음)를 대비해 fallback으로 빈 문자열을 반환합니다.
  return cell.textContent ?? "";
}

/**
 * extractValueAndConfigFromCell
 *
 * 주어진 HTMLTableCellElement에서 값과 answerConfig를 함께 추출합니다.
 * CampInputNode(camp-input 클래스)에서 data-answer-config를 파싱합니다.
 *
 * @param cell - 값을 추출할 HTML 테이블 셀 요소
 * @returns { value: string, answerConfig?: AnswerConfig }
 */
export function extractValueAndConfigFromCell(cell: HTMLTableCellElement): {
  value: string;
  answerConfig?: AnswerConfig;
} {
  const inputs = cell.querySelectorAll("input, select");

  if (inputs.length > 0) {
    const input = inputs[0] as HTMLInputElement | HTMLSelectElement;
    const value = input.value;

    // camp-input 클래스가 있는지 확인
    if (input.classList.contains("camp-input")) {
      const answerConfigAttr = input.getAttribute("data-answer-config");
      if (answerConfigAttr) {
        try {
          // HTML 엔티티 디코딩 후 JSON 파싱
          const decodedConfig = answerConfigAttr
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">");

          const answerConfig = JSON.parse(decodedConfig) as AnswerConfig;
          return { value, answerConfig };
        } catch (e) {
          console.error(
            "❌ answerConfig 파싱 오류:",
            e,
            "원본:",
            answerConfigAttr,
          );
        }
      }
    }

    return { value };
  }

  return { value: cell.textContent ?? "" };
}
